import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

export default function TeacherCalendar() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day'
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    type: 'exam',
    date: '',
    time: '',
    duration: 60,
    class: '',
    students: 0,
    description: ''
  });

  useEffect(() => {
    if (user?.uid) {
      fetchCalendarEvents();
    }
  }, [user, currentDate, view]);

  const fetchCalendarEvents = async () => {
    if (!user?.uid) {
      console.log('No user found');
      return;
    }

    try {
      const { db } = await import('../firebase.js');
      const { collection, query, where, getDocs } = await import('firebase/firestore');

      console.log('Fetching real assignments for teacher:', user.uid);
      
      // Get all assignments for this teacher
      const assignmentsQuery = query(
        collection(db, 'assignments'),
        where('teacherId', '==', user.uid),
        where('isActive', '==', true)
      );

      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const realEvents = [];

      for (const assignmentDoc of assignmentsSnapshot.docs) {
        const assignment = assignmentDoc.data();
        
        // Get assignee count for this assignment
        const assigneesSnapshot = await getDocs(
          collection(db, 'assignments', assignmentDoc.id, 'assignees')
        );
        const studentCount = assigneesSnapshot.size;

        // Determine event type based on assignment data
        let eventType = 'assignment';
        let eventColor = 'bg-blue-500';
        
        if (assignment.title?.toLowerCase().includes('exam')) {
          eventType = 'exam';
          eventColor = 'bg-red-500';
        } else if (assignment.title?.toLowerCase().includes('quiz')) {
          eventType = 'quiz';
          eventColor = 'bg-blue-500';
        } else if (assignment.title?.toLowerCase().includes('test')) {
          eventType = 'exam';
          eventColor = 'bg-red-500';
        }

        // Create events for both start and end dates if they exist
        if (assignment.startAt) {
          const startDate = assignment.startAt.toDate();
          realEvents.push({
            id: `${assignmentDoc.id}_start`,
            title: assignment.title || 'Untitled Assignment',
            type: eventType,
            date: startDate,
            duration: assignment.timeLimit || 60,
            class: assignment.className || 'General',
            students: studentCount,
            status: startDate > new Date() ? 'scheduled' : 'active',
            description: assignment.instructions || 'No description available',
            color: eventColor,
            assignmentId: assignmentDoc.id,
            eventSubType: 'starts'
          });
        }

        if (assignment.endAt && assignment.endAt.toDate().getTime() !== assignment.startAt?.toDate().getTime()) {
          const endDate = assignment.endAt.toDate();
          realEvents.push({
            id: `${assignmentDoc.id}_end`,
            title: `${assignment.title || 'Untitled Assignment'} (Due)`,
            type: eventType,
            date: endDate,
            duration: 0,
            class: assignment.className || 'General',
            students: studentCount,
            status: endDate > new Date() ? 'scheduled' : 'completed',
            description: `Deadline: ${assignment.instructions || 'No description available'}`,
            color: 'bg-orange-500',
            assignmentId: assignmentDoc.id,
            eventSubType: 'due'
          });
        }
      }

      console.log(`Found ${realEvents.length} real calendar events`);
      setEvents(realEvents);

    } catch (error) {
      console.error('Error fetching calendar events:', error);
      // Fallback to empty events if there's an error
      setEvents([]);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'exam': return '📝';
      case 'quiz': return '❓';
      case 'assignment': return '📋';
      case 'test': return '📄';
      case 'meeting': return '👥';
      case 'lab': return '🔬';
      case 'professional': return '🎓';
      default: return '📅';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCreateEvent = () => {
    setNewEvent({
      title: '',
      type: 'exam',
      date: '',
      time: '',
      duration: 60,
      class: '',
      students: 0,
      description: ''
    });
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const saveEvent = () => {
    if (selectedEvent) {
      // Update existing event
      setEvents(prev => prev.map(e => 
        e.id === selectedEvent.id ? { ...selectedEvent } : e
      ));
    } else {
      // Create new event
      const eventDateTime = new Date(`${newEvent.date}T${newEvent.time}`);
      const event = {
        id: Date.now(),
        ...newEvent,
        date: eventDateTime,
        status: 'scheduled',
        color: getColorForType(newEvent.type)
      };
      setEvents(prev => [...prev, event]);
    }
    setShowEventModal(false);
  };

  const getColorForType = (type) => {
    const colors = {
      exam: 'bg-red-500',
      quiz: 'bg-blue-500',
      assignment: 'bg-green-500',
      meeting: 'bg-purple-500',
      lab: 'bg-orange-500',
      professional: 'bg-indigo-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);
  const today = new Date();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <span className="mr-3">📅</span>
                Teaching Calendar
              </h1>
              <p className="text-gray-600 mt-1">Schedule and manage your academic events</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCreateEvent}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center"
              >
                <span className="mr-2">+</span>
                New Event
              </button>
              <Link
                to="/dashboard"
                className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Calendar Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 border mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ←
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                →
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setView('month')}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  view === 'month' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  view === 'week' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Week
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {dayNames.map(day => (
              <div key={day} className="p-2 text-center font-medium text-gray-600 text-sm">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {days.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day && day.toDateString() === today.toDateString();
              const isCurrentMonth = day && day.getMonth() === currentDate.getMonth();
              
              return (
                <div
                  key={index}
                  className={`min-h-[100px] p-1 border border-gray-200 ${
                    !day ? 'bg-gray-50' : isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${
                        isToday ? 'text-blue-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {day.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map(event => (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            className={`text-xs p-1 rounded text-white cursor-pointer hover:opacity-80 truncate ${event.color}`}
                            title={event.title}
                          >
                            <span className="mr-1">{getEventTypeIcon(event.type)}</span>
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 px-1">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {events
              .filter(event => new Date(event.date) >= today)
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .slice(0, 10)
              .map(event => (
                <div
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${event.color}`}></div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getEventTypeIcon(event.type)}</span>
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          <span>{event.class}</span>
                          {event.students > 0 && <span className="ml-2">• {event.students} students</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(event.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatTime(new Date(event.date))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedEvent ? 'Event Details' : 'Create New Event'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              {selectedEvent ? (
                <>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getEventTypeIcon(selectedEvent.type)}</span>
                    <div>
                      <h4 className="font-medium text-gray-900">{selectedEvent.title}</h4>
                      <p className="text-sm text-gray-600">{selectedEvent.class}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Date:</span>
                      <p>{new Date(selectedEvent.date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Time:</span>
                      <p>{formatTime(new Date(selectedEvent.date))}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Duration:</span>
                      <p>{selectedEvent.duration} minutes</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Students:</span>
                      <p>{selectedEvent.students || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {selectedEvent.description && (
                    <div>
                      <span className="font-medium text-gray-700">Description:</span>
                      <p className="text-sm text-gray-600 mt-1">{selectedEvent.description}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Event Title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  />
                  
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newEvent.type}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="exam">Exam</option>
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                    <option value="meeting">Meeting</option>
                    <option value="lab">Lab</option>
                    <option value="professional">Professional Development</option>
                  </select>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                    />
                    <input
                      type="time"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                  
                  <input
                    type="text"
                    placeholder="Class/Subject"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newEvent.class}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, class: e.target.value }))}
                  />
                  
                  <textarea
                    placeholder="Description"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  ></textarea>
                </>
              )}
            </div>
            
            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                {selectedEvent ? 'Close' : 'Cancel'}
              </button>
              {!selectedEvent && (
                <button
                  onClick={saveEvent}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  Create Event
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}